import React from 'react';
import { getOrgTermsContent } from '../../content/utils.js';
import SubHeader from '../SubHeader.jsx';
import PortfolioProjects from '../portfolio/PortfolioProjects.jsx';
import VerticalSeparator from '../VerticalSeparator.jsx';
import ParentPortfoliosActionDropdown from '../parent-portfolio/ParentPortfoliosActionDropdown.jsx';
import ToggleArrow from '../common/ToggleArrow.jsx';
import DateRangeDisplay from '../common/DateRangeDisplay.jsx';
import StatusDisplay from '../common/StatusDisplay.jsx';
import { useToggleSection } from '../../hooks/useToggleSection.js';
import {
  filterPortfoliosBySearch,
  findStatus,
} from '../../containers/portfolio/utils/index.js';
import { INDENT } from '../../constants/index.js';

const ParentPortfolioSection = ({
  parentPortfolio,
  searchResult,
  projects,
  projectStatuses,
  queryState,
  onDeleteProject,
  onDeletePortfolio,
  onDeleteParentPortfolio,
  isTopLevel = false,
}) => {
  const { isHidden, className, toggle, isExpanded } =
    useToggleSection(isTopLevel);
  const isNoParentPortfolio = parentPortfolio.id === 'no_parent_portfolio';
  const { startDate, endDate, projectStatusId } = parentPortfolio;
  const status = findStatus(projectStatuses, projectStatusId);

  const portfoliosToRender = filterPortfoliosBySearch(
    parentPortfolio.portfolios,
    searchResult,
    isNoParentPortfolio
  );

  if (!portfoliosToRender.length && queryState.ownerIds?.length) return null;

  return (
    <section className={className}>
      <SubHeader
        flex
        className={`${className} component-border-top`}
        medium
        style={{ borderTopColor: '#7F8FA4', backgroundColor: '#E9EFF5' }}
      >
        <div className="flex-col w-100">
          <div className="m-b-18">
            <h3>
              <button
                className="btn-clear bg-transparent flex"
                onClick={toggle}
                type="button"
              >
                <ToggleArrow isExpanded={isExpanded} />
                <div className="m-l-16">
                  {isNoParentPortfolio ? (
                    `No ${getOrgTermsContent('parentPortfolio')}`
                  ) : (
                    <React.Fragment>
                      {getOrgTermsContent('parentPortfolio')}:&nbsp;
                      <span>{parentPortfolio.name}</span>
                    </React.Fragment>
                  )}
                </div>
              </button>
            </h3>
          </div>
          <div className="flex">
            {!isNoParentPortfolio ? (
              <React.Fragment>
                <div className="m-l-28">
                  <DateRangeDisplay startDate={startDate} endDate={endDate} />
                </div>
                <VerticalSeparator />
                <StatusDisplay status={status} />
                <VerticalSeparator />
              </React.Fragment>
            ) : null}
            <div className="flex header-item">
              <strong
                className={`text-muted m-r-12 ${isNoParentPortfolio ? 'm-l-28' : 'm-l-0'}`}
              >
                {getOrgTermsContent('portfolios')}:{' '}
              </strong>
              <strong>
                {portfoliosToRender ? portfoliosToRender.length || '—' : '—'}
              </strong>
            </div>
          </div>
        </div>

        {parentPortfolio.permissions && !isNoParentPortfolio ? (
          <ParentPortfoliosActionDropdown
            parentPortfolio={parentPortfolio}
            parentPortfolioId={parentPortfolio.id}
            onDeleteParentPortfolio={onDeleteParentPortfolio}
          />
        ) : null}
      </SubHeader>
      {!isHidden ? (
        portfoliosToRender.length ? (
          portfoliosToRender.map((portfolio) => (
            <PortfolioProjects
              key={portfolio.id}
              portfolio={portfolio}
              queryState={queryState}
              organizationProjects={projects}
              projectStatuses={projectStatuses}
              onDeleteProject={onDeleteProject}
              onDeletePortfolio={onDeletePortfolio}
              indentLevel={INDENT.PARENT_PORTFOLIO_CHILD}
            />
          ))
        ) : !queryState.search ? (
          <SubHeader
            bgWhite
            small
            flex
            style={{
              paddingLeft: `${INDENT.PARENT_PORTFOLIO_CHILD}px`,
              minHeight: '60px',
            }}
          >
            <strong>
              <h4 className="text-muted">
                {`There are no ${getOrgTermsContent('portfolios')} yet.`}
              </h4>
            </strong>
          </SubHeader>
        ) : null
      ) : null}
    </section>
  );
};

const ParentPortfoliosList = ({
  parentPortfolios,
  searchResult,
  projects,
  projectStatuses,
  queryState,
  onDeleteProject,
  onDeletePortfolio,
  onDeleteParentPortfolio,
  isTopLevel,
}) => {
  return (
    <React.Fragment>
      {parentPortfolios.map((parentPortfolio) => (
        <ParentPortfolioSection
          key={parentPortfolio.id}
          parentPortfolio={parentPortfolio}
          searchResult={searchResult}
          projects={projects}
          projectStatuses={projectStatuses}
          queryState={queryState}
          onDeleteProject={onDeleteProject}
          onDeletePortfolio={onDeletePortfolio}
          onDeleteParentPortfolio={onDeleteParentPortfolio}
          isTopLevel={isTopLevel}
        />
      ))}
    </React.Fragment>
  );
};

export default ParentPortfoliosList;
